import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { useDrawers } from "@/hooks/useDrawers";
import { useAuth } from "@/hooks/useAuth";
import { Menu, X, Flower, User, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

export const Header = () => {
  const { t } = useI18n();
  const { user, isAdmin, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigation = [
    { key: 'nav.home', href: '/' },
    { key: 'nav.solutions', href: '/solutions' },
    { key: 'nav.catalog', href: '/catalog' },
    { key: 'nav.farms', href: '/farms' },
    { key: 'nav.logistics', href: '/logistics' },
    { key: 'nav.about', href: '/about' },
    { key: 'nav.contact', href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 glass-effect border-b border-border/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-andean rounded-lg flex items-center justify-center">
              <Flower className="w-5 h-5 text-andean-cream" />
            </div>
            <span className="font-display font-light text-base text-primary tracking-wide">
              Andean Bloom
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className="text-sm font-light text-foreground hover:text-primary transition-colors tracking-wide"
              >
                {t(item.key)}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/cms"
                className="text-sm font-light text-foreground hover:text-primary transition-colors tracking-wide"
              >
                CMS
              </Link>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* Desktop Auth */}
            <div className="hidden lg:flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {user.email}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={signOut}
                    className="gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </Button>
                </div>
              )}
            </div>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border/20 py-4">
            <nav className="flex flex-col space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  className="text-sm font-light text-foreground hover:text-primary transition-colors py-2 tracking-wide"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t(item.key)}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/cms"
                  className="text-sm font-light text-foreground hover:text-primary transition-colors py-2 tracking-wide"
                  onClick={() => setIsMenuOpen(false)}
                >
                  CMS
                </Link>
              )}
              
               {/* Mobile Auth */}
               <div className="pt-3 border-t border-border/20 flex flex-col gap-2">
                 {user && (
                   <>
                     <span className="text-sm text-muted-foreground">
                       {user.email}
                     </span>
                     <Button 
                       variant="outline" 
                       size="sm" 
                       onClick={signOut}
                       className="gap-2 self-start"
                     >
                       <LogOut className="w-4 h-4" />
                       Sign Out
                     </Button>
                   </>
                 )}
               </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};